import { m } from '@pricedrop/i18n';
import { createFileRoute } from '@tanstack/react-router';
import BarChart2 from 'lucide-react/dist/esm/icons/bar-chart-2';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Download from 'lucide-react/dist/esm/icons/download';
import Eye from 'lucide-react/dist/esm/icons/eye';
import Shield from 'lucide-react/dist/esm/icons/shield';
import Star from 'lucide-react/dist/esm/icons/star';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { buildCanonicalLink, buildJsonLd, buildMeta } from '@/lib/seo';
import { ssrForPublicOnly } from '@/lib/ssr-utils';

export const Route = createFileRoute('/extension/')({
  ssr: ssrForPublicOnly,
  head: () => ({
    meta: buildMeta({
      title: 'תוסף Pricedrop לדפדפן - מעקב מחירים KSP',
      description:
        'התקינו את תוסף Pricedrop החינמי לדפדפן וצפו בהיסטוריית מחירים ישירות בדפי מוצרים של KSP. זמין לכרום, פיירפוקס, אדג׳ וברייב.',
      canonicalUrl: '/extension',
      ogImage: '/extension_screenshots/1.jpeg',
    }),
    links: [buildCanonicalLink('/extension')],
    scripts: [
      buildJsonLd({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Pricedrop - KSP Price Tracker',
        url: 'https://pricedrop.co.il/extension',
        applicationCategory: 'BrowserExtension',
        operatingSystem: 'Chrome, Firefox, Edge, Brave',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'ILS',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          ratingCount: '3',
          bestRating: '5',
        },
      }),
    ],
  }),
  component: ExtensionPage,
});

function getSlides() {
  return [
    {
      src: '/extension_screenshots/1.jpeg',
      alt: m.extension_slide1Alt(),
      title: m.extension_slide1Title(),
      description: m.extension_slide1Description(),
    },
    {
      src: '/extension_screenshots/2.jpeg',
      alt: m.extension_slide2Alt(),
      title: m.extension_slide2Title(),
      description: m.extension_slide2Description(),
    },
    {
      src: '/extension_screenshots/3.jpeg',
      alt: m.extension_slide3Alt(),
      title: m.extension_slide3Title(),
      description: m.extension_slide3Description(),
    },
  ];
}

const reviews = [
  {
    name: 'Ohad Barda',
    content:
      'The one and only price tracker for KSP website! Kudos to the developers of this extension. THANK YOU!',
    stars: 5,
  },
  {
    name: 'Jenya Kravc',
    content: 'אהבתי, במיוחד עכשיו בתקופת המבצעים אפשר לראות מה באמת מבצע אמיתי ומה העלו בדיוק לפני',
    stars: 5,
  },
  {
    name: 'Inga Morozova',
    content: 'ממש נוח וקל לקנות במחיר משתלם כל הכבוד למפתח נותן פייט למחירים המוגזמים',
    stars: 5,
  },
];

const browsers = [
  {
    name: 'Chrome',
    link: 'https://chrome.google.com/webstore/detail/pricedrop-ksp-price-track/dnhjobakfagimkcckpboclgjjnloifga',
  },
  {
    name: 'Brave',
    link: 'https://chrome.google.com/webstore/detail/pricedrop-ksp-price-track/dnhjobakfagimkcckpboclgjjnloifga',
  },
  {
    name: 'Edge',
    link: 'https://chrome.google.com/webstore/detail/pricedrop-ksp-price-track/dnhjobakfagimkcckpboclgjjnloifga',
  },
  {
    name: 'Firefox',
    link: 'https://addons.mozilla.org/en-US/firefox/addon/pricedrop-ksp-price-tracker/',
  },
];

function getFeatures() {
  return [
    {
      icon: BarChart2,
      title: m.extension_featureChartsTitle(),
      description: m.extension_featureChartsDescription(),
    },
    {
      icon: Clock,
      title: m.extension_featureHistoryTitle(),
      description: m.extension_featureHistoryDescription(),
    },
    {
      icon: BarChart3,
      title: m.extension_featureStatsTitle(),
      description: m.extension_featureStatsDescription(),
    },
    {
      icon: Shield,
      title: m.extension_featurePrivacyTitle(),
      description: m.extension_featurePrivacyDescription(),
    },
  ];
}

function getSteps() {
  return [
    {
      number: '1',
      title: m.extension_step1Title(),
      description: m.extension_step1Description(),
      icon: Download,
    },
    {
      number: '2',
      title: m.extension_step2Title(),
      description: m.extension_step2Description(),
      icon: Eye,
    },
    {
      number: '3',
      title: m.extension_step3Title(),
      description: m.extension_step3Description(),
      icon: BarChart2,
    },
  ];
}

function ExtensionPage() {
  const slides = getSlides();
  const features = getFeatures();
  const steps = getSteps();

  return (
    <div className="flex flex-col">
      <section className="relative w-full py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-r from-brand/10 to-brand-accent/10" />
        <div className="container relative mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center gap-8 text-center">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl">
                {m.extension_heroTitle()}
                <span className="bg-gradient-to-r from-brand to-brand-accent bg-clip-text text-transparent">
                  {' '}
                  {m.extension_heroTitleAccent()}
                </span>
              </h1>
              <p className="mx-auto max-w-[800px] text-xl text-muted-foreground md:text-2xl">
                {m.extension_heroSubtitle()}
              </p>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold">{m.extension_downloadTitle()}</h2>
              <div className="flex flex-wrap justify-center gap-6">
                {browsers.map((browser) => (
                  <a
                    key={browser.name}
                    href={browser.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center gap-3 rounded-xl border-2 bg-card p-6 transition-all duration-300 hover:border-brand hover:shadow-lg"
                  >
                    <img
                      src={`/browsers_logos/${browser.name.toLowerCase()}.png`}
                      className="h-16 w-16 transition-transform duration-300 group-hover:scale-110"
                      alt={`${browser.name} logo`}
                      loading="lazy"
                    />
                    <div className="text-center">
                      <h3 className="font-semibold">{browser.name}</h3>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24">
        <div className="container mx-auto flex flex-col items-center px-4 md:px-6">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
              {m.extension_demoTitle()}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{m.extension_demoSubtitle()}</p>
          </div>

          <Carousel opts={{ loop: true, direction: 'rtl' }} className="relative w-full max-w-5xl">
            <CarouselContent>
              {slides.map((slide) => (
                <CarouselItem key={slide.src} className="flex items-center justify-center">
                  <div className="relative overflow-hidden rounded-2xl">
                    <img
                      src={slide.src}
                      alt={slide.alt}
                      className="h-auto max-h-[600px] w-full shadow-2xl"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                      <h3 className="text-xl font-bold text-white">{slide.title}</h3>
                      <p className="text-sm text-white/90">{slide.description}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      <section className="w-full bg-muted py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
              {m.extension_featuresTitle()}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{m.extension_featuresSubtitle()}</p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={feature.title}
                  className="group rounded-2xl transition-all duration-300 hover:border-brand hover:shadow-xl"
                >
                  <CardContent className="flex items-start gap-4 p-5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-brand-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-bold">{feature.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
              {m.extension_stepsTitle()}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{m.extension_stepsSubtitle()}</p>
          </div>

          <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-3">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.number} className="flex flex-col items-center text-center">
                  <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-r from-brand to-brand-accent text-brand-foreground shadow-lg">
                    <Icon className="h-8 w-8" />
                  </div>
                  <div className="mb-4 flex h-8 w-8 items-center justify-center rounded-full bg-brand/10 font-bold text-brand">
                    {step.number}
                  </div>
                  <h3 className="mb-3 text-xl font-bold">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full bg-muted py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
              {m.extension_reviewsTitle()}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{m.extension_reviewsSubtitle()}</p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {reviews.map((review) => (
              <Card
                key={review.name}
                className="rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                <CardContent className="p-8">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-bold">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-semibold">{review.name}</h4>
                      <div className="flex">
                        {Array.from({ length: review.stars }, (_, i) => `${review.name}-${i}`).map(
                          (key) => (
                            <Star key={key} className="h-4 w-4 fill-current text-yellow-500" />
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                  <blockquote className="text-muted-foreground">
                    &ldquo;{review.content}&rdquo;
                  </blockquote>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-gradient-to-r from-brand to-brand-accent py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center">
            <h2 className="mb-6 text-3xl font-bold tracking-tighter text-brand-foreground sm:text-4xl md:text-5xl">
              {m.extension_ctaTitle()}
            </h2>
            <p className="mb-8 text-xl text-brand-foreground/80">{m.extension_ctaSubtitle()}</p>
            <div className="flex flex-wrap justify-center gap-4">
              {browsers.slice(0, 2).map((browser) => (
                <Button
                  key={browser.name}
                  variant="outline"
                  size="lg"
                  className="gap-2 rounded-xl bg-background px-8 py-4 font-semibold text-foreground transition-all duration-300 hover:shadow-lg"
                  asChild
                >
                  <a href={browser.link} target="_blank" rel="noopener noreferrer">
                    <img
                      src={`/browsers_logos/${browser.name.toLowerCase()}.png`}
                      className="h-6 w-6"
                      alt={`${browser.name} logo`}
                      loading="lazy"
                    />
                    <span>{m.extension_ctaDownload({ browser: browser.name })}</span>
                  </a>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
